import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ScenarioRepository } from "../../domain/interfaces/ScenarioRepository";
import { ScenarioEntity } from "../../domain/scenario.entity";
import { scenariosQueryKeys } from "../../infrastructure/cache/ScenariosCacheStrategy";
import { useOrchestratorCache } from "../../../shared/hooks/useOrchestratorCache";

export function useScenarioMutations(propertyTicker: string, repository: ScenarioRepository) {
  const queryClient = useQueryClient();
  const { regenCache } = useOrchestratorCache();

  const onSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: scenariosQueryKeys.list(propertyTicker) });
    await regenCache("scenarios");
  };

  const createScenario = useMutation({
    mutationFn: (entity: ScenarioEntity) => repository.create(propertyTicker, entity),
    onSuccess,
  });

  const updateScenario = useMutation({
    mutationFn: (entity: ScenarioEntity) => repository.update(propertyTicker, entity),
    onSuccess,
  });

  const deleteScenario = useMutation({
    mutationFn: (id: string) => repository.delete(propertyTicker, id),
    onSuccess,
  });

  const duplicateScenario = useMutation({
    mutationFn: (id: string) => repository.duplicate(propertyTicker, id),
    onSuccess,
  });

  const reorderScenario = useMutation({
    mutationFn: ({ id, after }: { id: string; after: string }) =>
      repository.reorder(propertyTicker, id, after),
    onSuccess,
  });

  return {
    createScenario: createScenario.mutateAsync,
    updateScenario: updateScenario.mutateAsync,
    deleteScenario: deleteScenario.mutateAsync,
    duplicateScenario: duplicateScenario.mutateAsync,
    reorderScenario: reorderScenario.mutateAsync,
  };
}
